import { useContext, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../redux/store";
import { ToastContext } from "../../../contexts/ToastContext/ToastContext";
import {
  getFinanceReport,
  getFinanceReportTabData,
  getOfficeAccountReceivables,
  getOfficeAccountReceivablesPayee,
  getOfficeAccountReceivablesTopOverdueAccounts,
  getOfficeAccountReceivablesTotals,
  getOfficeAnnualPlanRevenueByPlan,
  getOfficeAnnualPlanTotals,
  setActiveFinanceTab,
} from "../../../redux/slices/FinanceSlice";
import moment from "moment";
import { ToastVariants } from "../../../models";
import { listOfficeData, setSelectedOffice } from "../../../redux/slices/OfficeSlice";
import { getMonthDropdownOptions, getMonthRange, getReportDateRange } from "../../../utils";
import { Office, OfficeState } from "../../../models/office.model";
import { Button, DatePicker, SelectWithSearch, Tabs } from "../../../components";
import DashboardTab from "./DashboardTab";
import ReportingTab from "./ReportingTab";
import CircularProgress from "@mui/material/CircularProgress";

const VISIBLE_OFFICE_STATES: OfficeState[] = [OfficeState.Published, OfficeState.Draft, OfficeState.Disabled];

const OfficeTab = () => {
  const dispatch = useDispatch<AppDispatch>();
  const toast = useContext(ToastContext);
  const { officeFinanceReport, loading, activeTab } = useSelector((state: RootState) => state.finance);
  const { officeList, selectedOffice } = useSelector((state: RootState) => state.office);
  const date = new Date();
  const monthName = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
  const [monthOptions, setMonthOptions] = useState<string[]>([]);
  const [selectedMonth, setSelectedMonth] = useState<string>("");
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const [selectedOfficeSearch, setSelectedOfficeSearch] = useState("");
  const [selectedMonthReportingTab, setSelectedMonthReportingTab] = useState("");

  date.setDate(1);
  const monthArray: string[] = [];
  for (let i = 0; i <= 11; i++) {
    monthArray.push(monthName[date.getMonth()] + " " + date.getFullYear());
    date.setMonth(date.getMonth() - 1);
  }

  const fetchData = (officeId: string, startDate: string, endDate: string, loadAllData: boolean) => {
    dispatch(getFinanceReport({ officeId, startDate: new Date(startDate), endDate: new Date(endDate) }));
    if (loadAllData) {
      dispatch(getOfficeAccountReceivablesTotals({ officeId }));
      dispatch(getOfficeAccountReceivablesPayee({ officeId }));
      dispatch(getOfficeAccountReceivablesTopOverdueAccounts({ officeId }));
      dispatch(getOfficeAccountReceivables({ officeId, page: 0 }));
      dispatch(getOfficeAnnualPlanTotals({ officeId }));
      dispatch(getOfficeAnnualPlanRevenueByPlan({ officeId }));
    }
  };

  const handleMonthChange = (e: string) => {
    setSelectedMonth(e);
    setStartDate("");
    setEndDate("");
    const selectedMonth = e.split(" ");
    const month = monthName.indexOf(selectedMonth[0]);
    const year = selectedMonth[1];
    const firstDay = moment([year, month]).startOf("month").format("YYYY-MM-DD");
    const lastDay = moment([year, month]).endOf("month").add(1, "day").format("YYYY-MM-DD");
    if (selectedOffice?.officeId) {
      fetchData(selectedOffice?.officeId, firstDay, lastDay, false);
    }
  };

  const applyFilter = () => {
    if (startDate > endDate) {
      toast?.openToast("End date should be greater then start date", 2000, ToastVariants.Error);
      return;
    }
    if (moment(endDate, "YYYY-MM-DD").diff(moment(startDate, "YYYY-MM-DD"), "year") > 0) {
      toast?.openToast("Date range should not be greater then one year", 2000, ToastVariants.Error);
      return;
    }
    const firstDay = moment(startDate, "YYYY-MM-DD").format("YYYY-MM-DD");
    const lastDay = moment(endDate, "YYYY-MM-DD").add(1, "day").format("YYYY-MM-DD");
    if (selectedOffice?.officeId) {
      setSelectedMonth("");
      fetchData(selectedOffice?.officeId, firstDay, lastDay, false);
    }
  };

  const handleTabChange = (index: number) => {
    if (index === 1 && !selectedOffice?.officeId) {
      toast?.openToast("Please select a single office from the dropdown to access reporting.", 2000, ToastVariants.Warn);
      return;
    }
    dispatch(setActiveFinanceTab({ index }));
  };

  const onOptionSelect = (value: Office) => {
    if (value.officeId === "") {
      handleTabChange(0);
    }
    setStartDate("");
    setEndDate("");
    dispatch(setSelectedOffice(value));
    setSelectedOfficeSearch("");
  };

  const changeReportTabMonth = (val: string) => {
    setSelectedMonthReportingTab(val);
    const isMTD = val.includes("MTD");
    const splitMonth = val.split(" ");
    const month = monthName.indexOf(splitMonth[0]);
    const { firstDay, lastDay } = getReportDateRange(month, val, isMTD);
    const mtd = isMTD ? "mtd" : undefined;

    dispatch(
      getFinanceReportTabData({
        officeId: selectedOffice?.officeId ?? "",
        startDate: new Date(firstDay),
        endDate: new Date(lastDay),
        mtd: mtd,
      })
    );
  };

  const loadData = (office: Office) => {
    const options = getMonthDropdownOptions(office.createdDate);
    setMonthOptions(options);
    const monthYear = moment().format("MMMM YYYY");
    setSelectedMonth(monthYear);
    const items = monthYear.split(" ");
    const { firstDay, lastDay } = getMonthRange(items[0], items[1]); // October 2024
    if (office?.officeId && firstDay && lastDay) {
      fetchData(office?.officeId, firstDay, lastDay, true);
    }
    if (options.length > 1) {
      const today = moment();
      const currentMonth = `${today.format("MMMM YYYY")} - MTD`;

      if (today.date() > 1) {
        setSelectedMonthReportingTab(currentMonth);
        changeReportTabMonth(currentMonth);
      } else {
        const defaultMonth = options[1]; // fallback to previous month or something else
        setSelectedMonthReportingTab(defaultMonth);
        changeReportTabMonth(defaultMonth);
      }
    }
  };

  useEffect(() => {
    if (selectedOffice) loadData(selectedOffice);
  }, [selectedOffice]);

  useEffect(() => {
    dispatch(listOfficeData({ page: 0, size: 100, sortType: "name,asc", officeStates: VISIBLE_OFFICE_STATES }));
  }, [dispatch]);

  const noRecordFound = !(
    officeFinanceReport?.totalRevenue ||
    officeFinanceReport?.discountsProvided ||
    officeFinanceReport?.feesOwedToDs ||
    officeFinanceReport?.netIncome ||
    officeFinanceReport?.dsCollected ||
    officeFinanceReport?.collectedByOffice ||
    officeFinanceReport?.outstandingPayments ||
    officeFinanceReport?.totalTaxApplicableRevenue ||
    officeFinanceReport?.taxCollected
  );

  let officesList: Office[] = [];
  if (officeList?.data?.content?.length) {
    officesList = [...officesList, ...officeList?.data?.content];
  }

  const renderFilters = () => {
    return (
      <div className="flex items-center flex-wrap relative gap-2">
        <div className="flex items-center justify-start p-0 xl:mb-0 mt-2 sm:mt-0">
          <select
            onChange={e => {
              handleMonthChange(e.target.value);
            }}
            disabled={!selectedOffice?.officeId}
            value={selectedMonth}
            className={`general-select w-52 space-x-0 bg-transparent`}
          >
            {!selectedMonth ? <option value={""}>Select Month</option> : null}
            {selectedOffice?.officeId ? (
              monthOptions.map((ele, index) => {
                return <option key={index}>{ele}</option>;
              })
            ) : (
              <option>Select Office First</option>
            )}
          </select>
        </div>
        <div className="flex items-center flex-wrap gap-2">
          <div className="w-28">
            <span className="text-base font-medium">or show From:</span>
          </div>
          <div className="w-40">
            <DatePicker
              label={""}
              name=""
              isRequired={false}
              placeholder={"MM/DD/YYYY"}
              value={startDate}
              onChange={val => {
                setStartDate(val);
              }}
              errorMsg={""}
              isError={false}
              disabled={false}
              isDob={false}
              isDarkBg={true}
            />
          </div>
          <span className="text-base font-medium pl-3 xl:pl-0 pr-3 xl:pr-0">To:</span>
          <div className="w-40">
            <DatePicker
              label={""}
              name=""
              isRequired={false}
              placeholder={"MM/DD/YYYY"}
              value={endDate}
              onChange={val => {
                setEndDate(val);
              }}
              errorMsg={""}
              isError={false}
              disabled={false}
              isDob={false}
              isDarkBg={true}
            />
          </div>
          <Button onClick={applyFilter} className="ml-3 xl:ml-0 mt-2 sm:mt-0" text="View" disabled={!startDate || !endDate || loading} />
        </div>
      </div>
    );
  };

  const reportingMonths = monthOptions?.length ? [...monthOptions] : [];
  reportingMonths.shift();
  const today = moment();
  const currentMonthMTD = today.date() > 1 ? `${today.format("MMMM YYYY")} - MTD` : null;

  return (
    <>
      <div className="flex items-center flex-wrap relative gap-2 mb-5">
        <div className="">
          <SelectWithSearch
            value={selectedOffice?.name ?? ""}
            onSearcSelect={setSelectedOfficeSearch}
            onSelect={onOptionSelect}
            label="Select an office to view the financial data"
            options={officesList.filter(o => `${o.name.toLowerCase()}`.includes(selectedOfficeSearch.toLowerCase())) || []}
            //extraClasses="w-52"
            placeholder={"Select Office"}
            isSearchBox={true}
            searchValue={selectedOfficeSearch}
            renderOption={option => {
              return <div>{option.name}</div>;
            }}
            renderValue={option => {
              const renderVal = officesList?.find(o => `${o.name}` === option);
              return renderVal ? `${renderVal.name}` : "";
            }}
            onSearchChange={(e, newValue) => {
              setSelectedOfficeSearch(newValue);
            }}
          />
        </div>
      </div>
      {loading ? (
        <CircularProgress size={40} color={"info"} style={{ marginLeft: "50%", marginTop: "10%" }} />
      ) : selectedOffice?.officeId ? (
        <>
          <div className="relative">
            <Tabs
              components={[
                <DashboardTab key="dashboard-tab" noRecordFound={noRecordFound} renderFilters={renderFilters()} />,
                <ReportingTab
                  key="reporting-tab"
                  monthOptions={monthOptions}
                  changeReportTabMonth={changeReportTabMonth}
                  selectedMonth={selectedMonthReportingTab}
                  selectedOffice={selectedOffice}
                  monthName={monthName}
                />,
              ]}
              options={["Dashboard", "Reporting"]}
              activeTab={activeTab}
              onClickTab={handleTabChange}
            />
            {activeTab === 1 && reportingMonths?.length ? (
              <div className="flex items-center justify-start absolute top-0 right-0">
                <select
                  className="general-select w-52"
                  onChange={e => {
                    changeReportTabMonth(e.target.value);
                  }}
                  value={selectedMonthReportingTab}
                >
                  {[...(currentMonthMTD ? [currentMonthMTD] : []), ...reportingMonths].map((ele: string, index: number) => {
                    return (
                      <option key={index} value={ele}>
                        {ele}
                      </option>
                    );
                  })}
                </select>
              </div>
            ) : null}
          </div>
        </>
      ) : null}
    </>
  );
};
export default OfficeTab;
